import { VariationInfo } from "@/interfaces";

import { appColors } from "@/constants/color";
const statusColors = {
  Completed: {
    statusColor: appColors.successText,
    bgColor: appColors.successBg,
    text: 'Completed',
  },
  Paid: {
    statusColor: appColors.successText,
    bgColor: appColors.successBg,
    text: 'Completed',
  },
  Cancelled: {
    statusColor: appColors.errorText,
    bgColor: appColors.statusCancelledBg,
    text: 'Cancelled',
  },
  Due: {
    statusColor: appColors.errorText,
    bgColor: appColors.statusCancelledBg,
    text: 'Cancelled',
  },
  Processing: {
    statusColor: appColors.statusProcessingText,
    bgColor: appColors.statusProcessingBg,
    text: 'Processing',
  },
  Shipped: {
    statusColor: appColors.statusShippedText,
    bgColor: appColors.statusShippedBg,
    text: 'Shipped',
  },
  Refunded: {
    statusColor: appColors.refundedStatusText,
    bgColor: appColors.refundedStatusBg,
    text: 'Refunded',
  },
  Returned: {
    statusColor: appColors.statusReturnedText,
    bgColor: appColors.statusReturnedBg,
    text: 'Returned',
  },
  default: {
    statusColor: appColors.textGray,
    bgColor: appColors.panelMutedBg,
    text: 'Unknown',
  },
  Premium: {
    statusColor: appColors.successText,
    bgColor: appColors.successBg,
    textStyle: {color: appColors.successText},
    containerStyle: {backgroundColor: appColors.successBg},
  },
  Active: {
    statusColor: appColors.statusProcessingText,
    bgColor: appColors.statusProcessingBg,
    textStyle: {color: appColors.statusProcessingText},
    containerStyle: {backgroundColor: appColors.statusProcessingBg},
  },
  Inactive: {
    statusColor: appColors.textGray,
    bgColor: appColors.panelMutedBg,
    textStyle: {color: appColors.textGray},
    containerStyle: {backgroundColor: appColors.panelMutedBg},
  },
  PartialPaid: {
    statusColor: appColors.textGray,
    bgColor: appColors.panelMutedBg,
    textStyle: {color: appColors.textGray},
    containerStyle: {backgroundColor: appColors.panelMutedBg},
  },
};
export const getStatusColors = (
  status: keyof typeof statusColors | undefined,
) => {
  return status ? statusColors[status] : statusColors.default;
};


export function dateFormateDDMMYYYY(date: Date): string {
  if (date) {
    const option: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    };
    return new Intl.DateTimeFormat('en-US', option).format(new Date(date));
  } else return '';
}


export function generateVariationSlug(variation: VariationInfo): string {
  const parts: string[] = [];

  // Helper function to add non-empty values to parts
  const addIfPresent = (value: string | number | undefined) => {
    if (value !== undefined && value !== '') {
      parts.push(String(value).toLowerCase());
    }
  };

  // Process each variation property
  if (variation?.size?.name) {
    addIfPresent(variation.size.name);
  }

  if (variation?.weight) {
    if (variation?.weight?.quantity) {
      addIfPresent(variation.weight.quantity);
    }
    if (variation?.weight?.unit) {
      addIfPresent(variation.weight.unit);
    }
  }

  if (variation?.color?.name) {
    addIfPresent(variation?.color?.name);
  }

  if (variation?.flavor?.name) {
    addIfPresent(variation.flavor.name);
  }

  // Generate the slug
  let slug = parts.join('-');

  // Clean the slug further
  slug = slug
    .replace(/[^\w-]+/g, '') // Remove non-word chars except hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with single
    .replace(/^-+/, '') // Trim hyphens from start
    .replace(/-+$/, ''); // Trim hyphens from end

  return slug; // Fallback if no properties provided
}


export function capitalizeFirstLetterOfWords(input: string): string {
  return input
    ?.toLocaleLowerCase()
    ?.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function isEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a == null || b == null) return false;
  if (typeof a !== 'object') return a === b;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!isEqual(a[i], b[i])) return false;
    }
    return true;
  }
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!isEqual(a[key], b[key])) return false;
  }

  return true;
}

export function pick<T extends object, K extends keyof T>(
  object: T,
  fields: K[],
): Pick<T, K> {
  const result = {} as Pick<T, K>;
  for (const field of fields) {
    if (Object.prototype.hasOwnProperty.call(object, field) && object[field]) {
      result[field] = object[field];
    }
  }
  return result;
}

export function convertToUTCDateTime(
  dateInput: Date | string | number,
): string {
  return `${new Date(dateInput)?.toISOString().split('Z')[0] + '+00:00'}`;
}

export function shortenInvoiceNumber(invoice: string): number {
  // remove first 8 chars (YYYYMMDD)
  let numberPart = invoice.slice(8);

  // convert to integer to remove leading zeros
  return parseInt(numberPart, 10);
}

// utils/dateFormatter.ts
export function formatDateToIST(dateString: string): string {
  const date = new Date(dateString);

  // Convert to IST (UTC+5:30)
  const options: Intl.DateTimeFormatOptions = {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };

  return new Intl.DateTimeFormat("en-IN", options).format(date);
}
export function formatTimeToIST(dateString: string): string {
  const date = new Date(dateString);

  const options: Intl.DateTimeFormatOptions = {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };

  return new Intl.DateTimeFormat("en-IN", options).format(date);
}
export function parseNumberInput(
  text: string,
  maxDecimalPlaces: number = 2,
  allowNegative: boolean = false,
): string {
  let input = text?.toString().trim();

  // Step 1: Handle negative sign
  let isNegative = false;
  if (allowNegative && input.startsWith('-')) {
    isNegative = true;
    input = input.slice(1);
  }

  // Step 2: Remove invalid characters except dot
  input = input?.replace(/[^0-9.]/g, '');

  // Step 3: Handle multiple dots (keep only first one)
  const dotIndex = input?.indexOf('.');
  if (dotIndex !== -1) {
    const intPart = input?.slice(0, dotIndex);
    let decimalPart = input?.slice(dotIndex + 1)?.replace(/\./g, '');

    // Limit decimal places
    decimalPart = decimalPart?.slice(0, maxDecimalPlaces);

    input = intPart + '.' + decimalPart;
  }

  // Step 4: Normalize integer part (remove leading zeros except before dot)
  let [intPart, decimalPart] = input?.split('.');
  if (intPart.length > 1 && intPart?.startsWith('0')) {
    intPart = String(Number(intPart));
  }

  // Step 5: Construct final string
  let final = decimalPart !== undefined ? `${intPart}.${decimalPart}` : intPart;

  // Step 6: Preserve trailing dot if user typed it
  if (input?.endsWith('.') && decimalPart === undefined) {
    final = `${intPart}.`;
  }

  if (isNegative) {
    final = `-${final}`;
  }

  return final;
}
